import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../axiosBaseQuery';
import companiesMakersApiParser, { type CompaniesMakersData } from './companiesMakersApiParser';

// ---------- Types ----------
export interface CompaniesMakers {
  id: string | number;
  srno: number;
  shortName: string;
  maker: string;
  status: string;
  actions: string;
}

export interface CompaniesMakersListResponse {
  data: CompaniesMakers[];
}

interface CreateCompaniesMakersPayload {
  companyId: number;
  shortName: string;
  active: boolean;
}

export interface GetCompaniesMakersListParams {
  active?: boolean;
  [key: string]: any;
}

// ---------- API Slice ----------
const companiesMakersApi = createApi({
  reducerPath: 'companiesMakersApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['CompaniesMakers', 'CompaniesMakersData'],
  endpoints: (builder) => ({
    getCompaniesMakersList: builder.query<
      CompaniesMakers[],
      GetCompaniesMakersListParams | undefined
    >({
      query: (params) => {
        const queryParams: any = {};
        if (params?.active !== undefined) {
          queryParams.active = params.active;
        }
        if (params?.supplierId !== undefined) {
          queryParams.supplierId = params.supplierId;
        }
        return {
          url: '/lubricant/makers',
          method: 'GET',
          params: queryParams,
        };
      },
      transformResponse: (res: CompaniesMakersListResponse | any): CompaniesMakers[] =>
        companiesMakersApiParser.CompaniesMakersListParser(res),
    }),

    getCompaniesMakersData: builder.query<CompaniesMakersData, void>({
      query: () => ({
        url: '/lubricant/companies/short?partner=maker',
        method: 'GET',
      }),
      transformResponse: (res: any): CompaniesMakersData => {
        const content = Array.isArray(res?.data?.content) ? res.data.content : [];
        const companies = content.map((c: any) => ({ value: String(c.id), label: String(c.name) }));
        return {
          companiesMakers: [],
          companies,
          statusOptions: [
            { value: 'Active', label: 'Active' },
            { value: 'Inactive', label: 'Inactive' },
          ],
        } as CompaniesMakersData;
      },
      providesTags: ['CompaniesMakersData'],
    }),

    createCompaniesMakers: builder.mutation<any, CreateCompaniesMakersPayload>({
      query: (payload) => ({
        url: '/lubricant/makers',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['CompaniesMakers', 'CompaniesMakersData'],
    }),

    updateCompaniesMakers: builder.mutation<
      any,
      { id: number; payload: { shortName: string; active: boolean } }
    >({
      query: ({ id, payload }) => ({
        url: `/lubricant/makers/${encodeURIComponent(id)}`,
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CompaniesMakers' as const, id: id.toString() },
        'CompaniesMakers',
        'CompaniesMakersData',
      ],
    }),

    deleteCompaniesMakers: builder.mutation<any, number>({
      query: (id) => ({
        url: `/lubricant/makers/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CompaniesMakers', 'CompaniesMakersData'],
    }),
  }),
});

// ---------- Exports ----------
export const companiesMakersReducer = companiesMakersApi.reducer;
export const companiesMakersMiddleware = companiesMakersApi.middleware;
export const companiesMakersReducerPath = companiesMakersApi.reducerPath;

export const {
  useGetCompaniesMakersListQuery,
  useGetCompaniesMakersDataQuery,
  useCreateCompaniesMakersMutation,
  useUpdateCompaniesMakersMutation,
  useDeleteCompaniesMakersMutation,
} = companiesMakersApi;
